import { observer } from "mobx-react-lite";
import { css } from "@/domains/emotion";
import { useAppStore, useGuestAppStore } from "@/store";
import { NoteEditorTopBarShareButton } from "@/components/note/editor/top-bar/note-editor-top-bar-share-button";
import { NoteEditorTopBarExtraActionsDropdownButton } from "@/components/note/editor/top-bar/note-editor-top-bar-extra-actions-dropdown-button";
import { NoteTopBarTrashedNoteBanner } from "@/components/note/editor/top-bar/note-top-bar-trashed-note-banner";
import { ZIndex } from "@/domains/design/constants";
import { AnyNoteVoiceSuggestion, INoteObservable } from "@/store/note/types";
import { PageElementRouter } from "@/app/outlets/PageElementRouter";
import styled from "@emotion/styled";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { MDSPanelWidths } from "@/mds/foundations";
import { OrganizeNote, OrganizeNoCollections } from "@/components/note/editor/top-bar/organize";
import { AboutNoteModal } from "@/components/note/editor/top-bar/about/about-note-modal";
import { useState, useCallback, useRef, useEffect } from "react";
import { MDSIconButton, MDSIconButtonSize, MDSIconKind, MDSSpacer, MDSSeparator } from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { Popover } from "react-aria-components";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds/components/text";
import { NoteObservable } from "@/store/note/NoteObservable";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { CleanUpIndicator } from "@/components/note/editor/top-bar/indicators/clean-up-indicator";
import { shouldShowVoiceIndicator } from "@/store/audio/pipeline/audio-pipeline.ui-projection";
import { NoteVersionHistoryMenuBar } from "@/components/note/version-history";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { FindInNoteModal } from "@/components/note/editor/top-bar/find-in-note-modal";
import { VoiceIndicator } from "@/components/note/editor/top-bar/indicators/voice-indicator";
import { clientEnvModule } from "@/modules/client-env";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import type { AboutNoteModalEntryPoint } from "@/domains/metrics/tracked-event";
import { resolveAudioRecordingDurationLabel } from "@/modules/audio-recording/recording-duration-label";
import { toastModule } from "@/modules/toast";
import { requestNoteReviewNow } from "@/store/note/request-note-review-now";
import { NoteConnectedWorkPopover } from "@/components/note/editor/top-bar/note-connected-work-popover";

interface NoteEditorTopBarProps {
  editorStore?: MemCommonEditorStore;
  note?: INoteObservable;
  containerWidth: number;
  isOutlineOpen?: boolean;
  onToggleOutline?: () => void;
  isHeadsUpFloatingWindowOpen?: boolean;
  onToggleHeadsUpFloatingWindow?: () => void;
}

export enum NoteSuggestionStrategy {
  VoiceRecording = "voice-recording",
  CleanUp = "clean-up",
}

export const NoteEditorTopBar = observer<NoteEditorTopBarProps>(function NoteEditorTopBar({
  editorStore,
  note,
  containerWidth,
  isOutlineOpen,
  onToggleOutline,
  isHeadsUpFloatingWindowOpen,
  onToggleHeadsUpFloatingWindow,
}) {
  return (
    <PageElementRouter
      standardModeElement={
        <StandardNoteComboBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
          isOutlineOpen={isOutlineOpen}
          onToggleOutline={onToggleOutline}
          isHeadsUpFloatingWindowOpen={isHeadsUpFloatingWindowOpen}
          onToggleHeadsUpFloatingWindow={onToggleHeadsUpFloatingWindow}
        />
      }
      guestModeElement={<GuestNoteEditorTopBar note={note} containerWidth={containerWidth} />}
    />
  );
});

const StandardNoteComboBar = observer<NoteEditorTopBarProps>(function StandardNoteComboBar({
  editorStore,
  note,
  containerWidth,
  isOutlineOpen,
  onToggleOutline,
  isHeadsUpFloatingWindowOpen,
  onToggleHeadsUpFloatingWindow,
}) {
  const page = useTabPageStore("notesViewPage");

  const isVersionHistoryActive =
    page.noteVersionHistory.currentNoteId && page.noteVersionHistory.currentNoteId === note?.id;

  return (
    <>
      <div style={{ display: isVersionHistoryActive ? "none" : "block" }}>
        <StandardNoteEditorTopBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
          isOutlineOpen={isOutlineOpen}
          onToggleOutline={onToggleOutline}
          isHeadsUpFloatingWindowOpen={isHeadsUpFloatingWindowOpen}
          onToggleHeadsUpFloatingWindow={onToggleHeadsUpFloatingWindow}
        />
      </div>
      <div style={{ display: isVersionHistoryActive ? "block" : "none" }}>
        <StandardNoteVersionHistoryTopBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
        />
      </div>
    </>
  );
});

const StandardNoteVersionHistoryTopBar = observer<NoteEditorTopBarProps>(
  function StandardNoteVersionHistoryTopBar({
    editorStore: _editorStore,
    note: _note,
    containerWidth: _containerWidth,
  }) {
    return (
      <>
        <Container $variant="version-history">
          <NoteVersionHistoryMenuBar />
        </Container>
      </>
    );
  }
);

const StandardNoteEditorTopBar = observer<NoteEditorTopBarProps>(function StandardNoteEditorTopBar({
  editorStore,
  note,
  containerWidth,
  isOutlineOpen,
  onToggleOutline,
  isHeadsUpFloatingWindowOpen,
  onToggleHeadsUpFloatingWindow,
}) {
  const { store } = useAppStore();
  const page = useTabPageStore("notesViewPage");

  const noteRecording = store.audioRecordings.getNoteRecording({
    noteId: note?.id ?? null,
  });
  const latestAudioRecordingForNote = noteRecording.recording;
  const activeRecording = store.audio.activeRecording;
  const activeRecordingForNote =
    activeRecording?.associatedNoteId === note?.id ? activeRecording : null;
  const voiceIndicatorRecording = activeRecordingForNote ?? latestAudioRecordingForNote ?? null;
  const voiceIndicatorDurationLabel = resolveAudioRecordingDurationLabel({
    activeRecording: voiceIndicatorRecording,
    noteRecordings: [noteRecording.recording, ...(noteRecording.recordings ?? [])],
    handoffTransition: store.audio.detectedMeetingHandoffTransition,
    noteId: note?.id ?? null,
    fallbackDurationLabel: voiceIndicatorRecording?.formattedDuration ?? "00:00",
  });

  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const openAboutModal = useCallback(
    (entryPoint: AboutNoteModalEntryPoint) => {
      if (note) {
        trackEvent(TrackedEvent.AboutNoteModalView, {
          note_id: note.id,
          entry_point: entryPoint,
          can_edit_created_at: note.canEditCreatedAt,
        });
      }
      setAboutModalOpen(true);
    },
    [note]
  );
  const [organizeIsOpen, setOrganizeIsOpen] = useState(false);
  const [mobileTooltipOpen, setMobileTooltipOpen] = useState(false);
  const [voiceIndicatorToggleRequestCount, setVoiceIndicatorToggleRequestCount] = useState(0);
  const [cleanUpIndicatorToggleRequestCount, setCleanUpIndicatorToggleRequestCount] = useState(0);
  const [isRequestingNoteReview, setIsRequestingNoteReview] = useState(false);
  const microphoneButtonRef = useRef<HTMLButtonElement>(null);
  const headsUpPopoverToggleRequestIdRef = useRef(
    store.floatingAgent.headsUpPopoverToggleRequestId
  );
  const findInNoteAnchorRef = useRef<HTMLSpanElement>(null);
  const acceptingVoiceCleanupChangesRef = useRef(false);

  const toggleOrganizeIsOpen = useCallback(() => {
    setOrganizeIsOpen(!organizeIsOpen);
  }, [organizeIsOpen]);

  const isCollapsed = containerWidth > 0 && containerWidth <= MDSPanelWidths.narrow;

  const hasSuggestion = editorStore?.isShowingDifferences;
  const suggestionData = editorStore?.noteObservable?.suggestionData;
  const suggestionKind = suggestionData?.kind;

  const suggestionStrategy = (() => {
    switch (suggestionKind) {
      case "VOICE_SESSION_V1":
      case "VOICE_SESSION_REFINE_V1":
        return NoteSuggestionStrategy.VoiceRecording;
      case "NOTE_CLEANUP_V1":
      case "NOTE_CLEANUP_REFINE_V1":
        return NoteSuggestionStrategy.CleanUp;
      default:
        return null;
    }
  })();

  const noteVoiceSuggestion: AnyNoteVoiceSuggestion | null = (() => {
    if (!note?.id) return null;

    if (suggestionStrategy !== NoteSuggestionStrategy.VoiceRecording) {
      return null;
    }

    if (!suggestionData) {
      return null;
    }

    return {
      noteId: note.id,
      data: suggestionData,
    } as AnyNoteVoiceSuggestion;
  })();

  const isActiveRecordingForNote = Boolean(
    note?.id && store.audio.activeRecordingNoteId === note.id
  );

  const isVoiceIndicatorVisible = (() => {
    if (!note?.id) return false;

    // Show indicator immediately when this note has the active recording,
    // even before the recording observable loads from the lazy store.
    if (isActiveRecordingForNote) return true;

    return shouldShowVoiceIndicator({
      recording: voiceIndicatorRecording ?? null,
      hasVoiceSuggestion: Boolean(noteVoiceSuggestion),
      snapshot: voiceIndicatorRecording
        ? store.audio.pipeline.getSnapshot(voiceIndicatorRecording.id)
        : null,
    });
  })();

  const isCleanUpIndicatorVisible = suggestionStrategy === NoteSuggestionStrategy.CleanUp;
  const isVoiceOrCleanUpIndicatorVisible = isVoiceIndicatorVisible || isCleanUpIndicatorVisible;

  const isMobileBrowser = clientEnvModule.isMobileBrowser();
  const isMicrophoneDisabled = store.audio.recording.isInitializing;
  const editorContentIsEmpty = editorStore?.editor?.isEmpty ?? editorStore?.isNoteEmpty ?? false;
  const noteIsStillEmptyBeforeEditorReports =
    note instanceof NoteObservable && note.isNoteContentInOriginalState && !editorStore?.edited;
  const autoOrganizeDisabledReason =
    editorContentIsEmpty || noteIsStillEmptyBeforeEditorReports
      ? "Add content before auto organizing"
      : undefined;
  const isOutlineButtonDisabled = !onToggleOutline;
  const isOutlineButtonToggled = Boolean(isOutlineOpen);
  const outlineButtonLabel = (() => {
    if (isOutlineOpen) return "Hide outline";

    return "Show outline";
  })();
  const shouldShowHeadsUpFloatingWindow = store.floatingAgent.isHeadsUpFloatingWindowEnabled;
  const headsUpButtonLabel = "Toggle Heads Up";

  const handleToggleHeadsUpFloatingWindow = useCallback(() => {
    onToggleHeadsUpFloatingWindow?.();
  }, [onToggleHeadsUpFloatingWindow]);

  const handleToggleOutline = useCallback(() => {
    if (!onToggleOutline) return;

    onToggleOutline();
  }, [onToggleOutline]);

  const handleRequestNoteReview = useCallback(async () => {
    if (!note || isRequestingNoteReview) return;

    setIsRequestingNoteReview(true);
    try {
      await requestNoteReviewNow(note.id);
      toastModule.triggerToast({ content: "Note review requested" });
    } catch (error) {
      toastModule.triggerToast({
        content: error instanceof Error ? error.message : "Could not request a note review.",
      });
    } finally {
      setIsRequestingNoteReview(false);
    }
  }, [isRequestingNoteReview, note]);

  useEffect(() => {
    const toggleRequestId = store.floatingAgent.headsUpPopoverToggleRequestId;

    if (toggleRequestId === headsUpPopoverToggleRequestIdRef.current) {
      return;
    }

    headsUpPopoverToggleRequestIdRef.current = toggleRequestId;

    if (!shouldShowHeadsUpFloatingWindow) {
      return;
    }

    onToggleHeadsUpFloatingWindow?.();
  }, [
    onToggleHeadsUpFloatingWindow,
    shouldShowHeadsUpFloatingWindow,
    store.floatingAgent.headsUpPopoverToggleRequestId,
  ]);

  // Auto-hide mobile tooltip after 3 seconds
  useEffect(() => {
    if (mobileTooltipOpen && isMobileBrowser) {
      const timer = setTimeout(() => {
        setMobileTooltipOpen(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [mobileTooltipOpen, isMobileBrowser]);

  useEffect(() => {
    if (!page.organizeMenuOpenRequestCount) {
      return;
    }

    setOrganizeIsOpen(true);
    page.consumeOpenOrganizeMenuRequest?.();
  }, [page, page.organizeMenuOpenRequestCount]);

  useEffect(() => {
    if (!page.voiceCleanupPanelToggleRequestCount) {
      return;
    }

    if (isCleanUpIndicatorVisible) {
      setCleanUpIndicatorToggleRequestCount(requestCount => requestCount + 1);
    } else if (isVoiceIndicatorVisible) {
      setVoiceIndicatorToggleRequestCount(requestCount => requestCount + 1);
    }

    page.consumeToggleVoiceCleanupPanelRequest?.();
  }, [
    isCleanUpIndicatorVisible,
    isVoiceIndicatorVisible,
    page,
    page.voiceCleanupPanelToggleRequestCount,
  ]);

  const acceptVisibleVoiceCleanupChanges = useCallback(async () => {
    if (!editorStore) {
      return;
    }

    if (acceptingVoiceCleanupChangesRef.current) {
      return;
    }

    if (!isVoiceOrCleanUpIndicatorVisible) {
      return;
    }

    if (
      suggestionStrategy !== NoteSuggestionStrategy.VoiceRecording &&
      suggestionStrategy !== NoteSuggestionStrategy.CleanUp
    ) {
      return;
    }

    if (!editorStore.noteObservable?.isSuggestionActive) {
      return;
    }

    acceptingVoiceCleanupChangesRef.current = true;

    try {
      await editorStore.closeSuggestion(true);
    } finally {
      acceptingVoiceCleanupChangesRef.current = false;
    }
  }, [editorStore, isVoiceOrCleanUpIndicatorVisible, suggestionStrategy]);

  useEffect(() => {
    if (!page.voiceCleanupChangesAcceptRequestCount) {
      return;
    }

    page.consumeAcceptVoiceCleanupChangesRequest?.();
    void acceptVisibleVoiceCleanupChanges();
  }, [acceptVisibleVoiceCleanupChanges, page, page.voiceCleanupChangesAcceptRequestCount]);

  // Close mobile tooltip when clicking outside
  useEffect(() => {
    if (!mobileTooltipOpen || !isMobileBrowser) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        microphoneButtonRef.current &&
        !microphoneButtonRef.current.contains(event.target as Node)
      ) {
        setMobileTooltipOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [mobileTooltipOpen, isMobileBrowser]);

  if (!note) return null;
  if (note.isTrashed) return <NoteTopBarTrashedNoteBanner note={note} />;

  const collections = note.collectionList?.allCollections || [];

  return (
    <>
      <Container $variant={hasSuggestion ? "suggestion" : "default"}>
        {collections.length === 0 && (
          <OrganizeNoCollections
            note={note}
            onToggleOrganizeIsOpen={toggleOrganizeIsOpen}
            renderTrigger={trigger => (
              <OrganizeNote
                note={note}
                showAutoOrganize
                autoOrganizeDisabledReason={autoOrganizeDisabledReason}
                organizeIsOpen={organizeIsOpen}
                onOrganizeIsOpenChange={setOrganizeIsOpen}
              >
                {trigger}
              </OrganizeNote>
            )}
          />
        )}
        {collections.length > 0 && (
          <OrganizeNote
            note={note}
            showAutoOrganize
            autoOrganizeDisabledReason={autoOrganizeDisabledReason}
            organizeIsOpen={organizeIsOpen}
            onOrganizeIsOpenChange={setOrganizeIsOpen}
          >
            <MDSTooltip
              config={getShortcutTooltipConfig({
                action: "organize_selected_notes",
                label: "Organize",
                placement: MDSTooltipPlacement.Top,
              })}
            >
              <StyledHashtagButton
                iconKind={MDSIconKind.Collection}
                onClick={toggleOrganizeIsOpen}
              />
            </MDSTooltip>
          </OrganizeNote>
        )}
        <ItemListRowFeaturedContent
          compact
          collections={sortCollectionsForChips(collections)}
          noteId={note.id}
          onCollectionOverflowClick={() => openAboutModal("collection_overflow")}
        />
        <Spacer />
        <>
          {note instanceof NoteObservable && (
            <IconsContainer>
              {isVoiceIndicatorVisible && editorStore && (
                <VoiceIndicator
                  audioRecording={voiceIndicatorRecording}
                  noteSuggestion={noteVoiceSuggestion}
                  editorStore={editorStore}
                  isActiveRecording={isActiveRecordingForNote}
                  toggleRequestCount={voiceIndicatorToggleRequestCount}
                  durationLabelOverride={voiceIndicatorDurationLabel}
                />
              )}
              {isCleanUpIndicatorVisible && editorStore && (
                <CleanUpIndicator
                  editorStore={editorStore}
                  toggleRequestCount={cleanUpIndicatorToggleRequestCount}
                />
              )}
              {!isVoiceIndicatorVisible && (
                <>
                  {isMobileBrowser ? (
                    <>
                      <StyledMicrophoneButton
                        ref={microphoneButtonRef}
                        size={MDSIconButtonSize.Medium}
                        iconKind={MDSIconKind.Microphone}
                        $isDisabled={isMobileBrowser}
                        aria-disabled={isMobileBrowser}
                        onClick={() => setMobileTooltipOpen(true)}
                      />
                      <Popover
                        triggerRef={microphoneButtonRef}
                        placement="top"
                        isOpen={mobileTooltipOpen}
                        onOpenChange={setMobileTooltipOpen}
                        isNonModal
                        style={{ zIndex: ZIndex.FloatingTooltip }}
                      >
                        <TooltipContent>
                          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.OnAccent}>
                            Voice recording is not available on mobile browsers
                          </MDSText>
                        </TooltipContent>
                      </Popover>
                    </>
                  ) : (
                    <MDSTooltip
                      config={{
                        label: isMicrophoneDisabled
                          ? "Starting voice recording..."
                          : "Start voice recording",
                        placement: MDSTooltipPlacement.Top,
                      }}
                    >
                      <MDSIconButton
                        size={MDSIconButtonSize.Medium}
                        iconKind={MDSIconKind.Microphone}
                        isDisabled={isMicrophoneDisabled}
                        onClick={() => {
                          if (isMicrophoneDisabled) return;
                          void store.audio.createAndStartAudioRecording({
                            associatedNoteId: note.id,
                            calendarEventId: note.associatedSpaceAccountCalendarEventId,
                            preferDetectedMeetingWindow: true,
                          });
                        }}
                      />
                    </MDSTooltip>
                  )}
                </>
              )}
              {!isCollapsed && (
                <MDSTooltip
                  config={getShortcutTooltipConfig({
                    action: "cleanup_note",
                    label: "Clean up notes in a single click",
                    placement: MDSTooltipPlacement.Top,
                  })}
                >
                  <MDSIconButton
                    size={MDSIconButtonSize.Medium}
                    iconKind={
                      note.isProcessingSuggestion ? MDSIconKind.Spinner : MDSIconKind.Cleanup
                    }
                    isDisabled={note.isProcessingSuggestion}
                    onClick={() => note.cleanup()}
                  />
                </MDSTooltip>
              )}
              {!isCollapsed && <TopBarSeparator orientation="vertical" />}
            </IconsContainer>
          )}
          {shouldShowHeadsUpFloatingWindow ? (
            <>
              <OutlineButtonSlot data-test-id="note-heads-up-top-bar-button">
                <MDSTooltip
                  config={getShortcutTooltipConfig({
                    action: "show_heads_up",
                    label: headsUpButtonLabel,
                    placement: MDSTooltipPlacement.Top,
                  })}
                >
                  <HeadsUpTopBarButton
                    aria-label={headsUpButtonLabel}
                    aria-pressed={Boolean(isHeadsUpFloatingWindowOpen)}
                    size={MDSIconButtonSize.Medium}
                    iconKind={MDSIconKind.HeadsUp}
                    isToggled={Boolean(isHeadsUpFloatingWindowOpen)}
                    onClick={handleToggleHeadsUpFloatingWindow}
                  />
                </MDSTooltip>
              </OutlineButtonSlot>
            </>
          ) : null}
          {store.agentOverlay.isFeatureEnabled &&
          store.spaceAccountFeatureFlags.getFlagStatus("mem-agent-intent-model-v2") === true ? (
            <NoteConnectedWorkPopover
              isReviewing={isRequestingNoteReview}
              noteId={note.id}
              onRequestReview={note.canWrite ? () => void handleRequestNoteReview() : undefined}
            />
          ) : null}
          <OutlineButtonSlot data-test-id="note-outline-top-bar-button">
            <MDSTooltip
              config={{
                label: outlineButtonLabel,
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton
                aria-label={outlineButtonLabel}
                aria-pressed={isOutlineButtonToggled}
                size={MDSIconButtonSize.Medium}
                iconKind={MDSIconKind.ListTree}
                isDisabled={isOutlineButtonDisabled}
                isToggled={isOutlineButtonToggled}
                onClick={isOutlineButtonDisabled ? undefined : handleToggleOutline}
              />
            </MDSTooltip>
          </OutlineButtonSlot>
          <NoteEditorTopBarExtraActionsDropdownButton
            note={note}
            editorStore={editorStore}
            isCollapsed={isCollapsed}
            onViewNoteInfo={() => openAboutModal("extra_actions_dropdown")}
          />
          <FindInNoteAnchor ref={findInNoteAnchorRef} aria-hidden="true" />
          <FindInNoteModal
            store={editorStore}
            triggerRef={findInNoteAnchorRef}
            positionKey={containerWidth}
          />
        </>
      </Container>

      <AboutNoteModal
        isOpen={aboutModalOpen}
        onClose={() => setAboutModalOpen(false)}
        note={note}
        canShowVersionHistory={
          note.isAvailable &&
          !note.isSuggestionActive &&
          (note instanceof NoteObservable
            ? note.isNoteContentDocumentLoaded && !note.isNoteContentInOriginalState
            : true)
        }
        isShowingVersionHistory={page.noteVersionHistory.currentNoteId === note.id}
        onViewVersionHistory={() => page.noteVersionHistory.openNoteVersionHistory(note.id)}
      />
    </>
  );
});

const GuestNoteEditorTopBar = observer<NoteEditorTopBarProps>(function GuestNoteEditorTopBar({
  note,
}) {
  const { guestStore } = useGuestAppStore();
  return (
    <div className={topBarStyles}>
      <MDSSpacer />
      <NoteEditorTopBarShareButton note={note} shareSheetModal={guestStore.shareSheetModal} />
    </div>
  );
});

export const NOTE_EDITOR_TOP_BAR_HEIGHT = "40px";

const topBarStyles = css({
  alignItems: "center",
  display: "flex",
  gap: "4px",
  height: NOTE_EDITOR_TOP_BAR_HEIGHT,
  left: 0,
  // Use padding, not margin.
  padding: "6px 16px 0 16px",
  position: "relative",
  top: 0,
  width: "100%",
  zIndex: ZIndex.NavBar, // Above the editor so it scrolls under.

  "@media (max-width: 900px)": {
    padding: "0 8px",
  },
});

const Container = styled.div<{ $variant?: "default" | "suggestion" | "version-history" }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["5"]} 0 ${theme.spacing["7"]}`};
  ${({ $variant, theme }) =>
    $variant === "suggestion" && `background-color: ${theme.colors.background.diff};`};
  ${({ $variant, theme }) =>
    $variant === "version-history" &&
    `background-color: ${theme.colors.background.button.emphasisLight.default};`};

  ${({ theme }) => `
    ${theme.mediaQueries.rules.phone} {
      padding-left: ${theme.spacing[4]};
      padding-right: ${theme.spacing[4]};
    }
    `}
`;

const Spacer = styled.div`
  flex: 1;
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TopBarSeparator = styled(MDSSeparator)`
  height: 20px;
  align-self: center;
`;

const OutlineButtonSlot = styled.span`
  display: inline-flex;
`;

const HeadsUpTopBarButton = styled(MDSIconButton)`
  &[aria-pressed="true"] {
    background-color: ${({ theme }) => theme.colors.background.button.default.hover};
  }

  &[aria-pressed="true"] .mds-icon {
    color: ${({ theme }) => theme.colors.icon.primary};
    fill: ${({ theme }) => theme.colors.icon.primary};
  }
`;

const FindInNoteAnchor = styled.span`
  flex: 0 0 0;
  height: 32px;
  pointer-events: none;
  width: 0;
`;

const StyledHashtagButton = styled(MDSIconButton)`
  margin-right: ${({ theme }) => theme.spacing[1]};
`;

const StyledMicrophoneButton = styled(MDSIconButton)<{ $isDisabled: boolean }>`
  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    cursor: not-allowed;
    opacity: 0.4;

    &[data-hovered]:not([data-disabled]) {
      background-color: unset;
      transition: unset;
    }
    `}
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-block: ${({ theme }) => theme.spacing["1"]};
  padding-inline: ${({ theme }) => theme.spacing["2"]};
  background-color: ${({ theme }) => theme.colors.surface.tooltip};
  border-radius: 6px;
  width: fit-content;
`;
